import { Drawer as AntdDrawer } from 'antd';
import type { ReactNode } from 'react';

import type { ButtonVariant } from '@/components/common/button';

import { OverlayChrome, OverlayFooter } from '../overlay';

import styles from './drawer.module.scss';

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  width?: number | string;
  maskClosable?: boolean;
  closeOnEsc?: boolean;
  loading?: boolean;
  /** Extra class on the scrolling body region — for panels that own their own padding/layout. */
  bodyClassName?: string;
  children: ReactNode;
  primaryText?: string;
  onPrimary?: () => void;
  primaryVariant?: ButtonVariant;
  primaryDanger?: boolean;
  /** Holds the primary button disabled — e.g. a FormDrawer whose form is not yet valid. */
  primaryDisabled?: boolean;
  /** id of the element explaining why the primary action is unavailable. */
  primaryDescribedBy?: string;
  cancelText?: string;
  onCancel?: () => void;
  /** Internal: used by FormDrawer to wrap body + footer in a <form>. */
  onSubmit?: React.ComponentProps<'form'>['onSubmit'];
  /** Internal: makes the primary footer button type="submit". */
  primaryIsSubmit?: boolean;
  /**
   * This drawer is opened **over another drawer**, which is already dimming the page.
   *
   * Renders this drawer's own mask transparent: one scrim, not two stacked (grilling Q5 /
   * ADR-0019). The element itself is still there on purpose — it is what an outside click lands
   * on, so the top drawer closes and the one underneath stays open. `mask={false}` would remove
   * the element entirely and let the click reach the lower drawer's mask, closing both.
   */
  stacked?: boolean;
};

export function Drawer({
  open,
  onClose,
  title,
  width = 464,
  maskClosable = true,
  closeOnEsc = true,
  loading = false,
  bodyClassName,
  children,
  primaryText,
  onPrimary,
  primaryVariant = 'neutral',
  primaryDanger,
  primaryDisabled,
  primaryDescribedBy,
  cancelText = 'Cancel',
  onCancel,
  onSubmit,
  primaryIsSubmit = false,
  stacked = false,
}: DrawerProps) {
  const footer = (
    <OverlayFooter
      primaryText={primaryText}
      onPrimary={onPrimary}
      isSubmit={primaryIsSubmit}
      primaryVariant={primaryVariant}
      primaryDanger={primaryDanger}
      disabled={primaryDisabled}
      describedBy={primaryDescribedBy}
      loading={loading}
      cancelText={cancelText}
      onCancel={onCancel ?? onClose}
    />
  );

  return (
    <AntdDrawer
      open={open}
      onClose={onClose}
      placement="right"
      width={width}
      mask={{ closable: maskClosable }}
      keyboard={closeOnEsc}
      closable={false}
      title={null}
      footer={null}
      destroyOnHidden
      className={styles.drawer}
      // `className` lands on the inner `.ant-drawer-section`, not the `.ant-drawer` root that
      // directly parents `.ant-drawer-mask` — `rootClassName` is the prop that reaches the root,
      // which is what the `.stacked` selector in drawer.module.scss depends on.
      rootClassName={stacked ? styles.stacked : undefined}
      classNames={{ body: styles.bodyReset }}
    >
      <OverlayChrome
        title={title}
        onClose={onClose}
        footer={footer}
        onSubmit={onSubmit}
        bodyClassName={bodyClassName}
      >
        {children}
      </OverlayChrome>
    </AntdDrawer>
  );
}
